import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AnimationEvent } from '@angular/animations';

import {
    combineLatest,
    distinctUntilChanged,
    filter as rxjsFilter,
    filter,
    map,
    Observable,
    of,
    Subject,
    Subscription,
    switchMap,
    take,
    BehaviorSubject,
    tap,
    merge
} from 'rxjs';
import { remove } from 'lodash';

import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { MediaSubscriptionMediaTypeEnum } from '../../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { ScrollPanelComponent } from '../../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { ConfirmDialogModel } from '../../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { FilterChipsComponent } from '../../../../../Common/Filter/components/filterChips/filter-chips.component';
import { DuplicatesStatusEnum } from '../../../../../Common/Filter/enums/duplicates-status.enum';
import { FilterTypeDuplicatesModel } from '../../../../../Common/Filter/models/filter-type-duplicates.model';
import { AppearCondition, AutocompleteCustomOption } from '../../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ChipContainerComponent } from '../../../../../Common/Forms/components/ChipContainer/chip-container.component';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { DateQueryModel } from '../../../../../Common/Forms/models/date-query.model';
import {
    MentionBadge,
    mentionBadgesAll,
    mentionBadgesSaved
} from '../../../../../Common/Mentions/components/MentionCard/mention-card.component';
import { MentionListComponent } from '../../../../../Common/Mentions/components/MentionList/mention-list.component';
import { Mention } from '../../../../../Common/Mentions/models/mention';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { KeywordHighlightModel } from '../../../../../Common/Misc/models/keyword-highlight.model';
import { monitoringSortTypeToSearchCriteriaSortFieldMap } from '../../../../../Common/MonitorShared/enum/monitoring-sort-types.enum';
import { TagModel } from '../../../../../Common/Tags/models/tag.model';
import { TagsService } from '../../../../../Common/Tags/services/tags.service';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { UserAppMonitoringDisplaySettingsModel } from '../../../../../Core/Users/models/user-app-default-display-preferences.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { SearchErrorsEnum } from '../../../../Core/enums/search-errors.enum';
import { SearchStatesEnum } from '../../../enums/search-states.enum';
import { SearchError } from '../../../../Core/errors/search.error';
import { SearchRangeModel } from '../../../../Core/models/search-range.model';
import { SearchStateErrorModel } from '../../../../Core/models/search-state-error.model';
import { MediaSubscriptionService } from '../../../../Core/services/media-subscription.service';
import { FolderService } from '../../../../Core/services/folder.service';
import { MentionSearchService } from '../../../../Core/services/mention-search.service';
import { MentionsService } from '../../../../Core/services/mentions.service';
import { MonitorConfigService } from '../../../../../Common/MonitorShared/services/monitor-config.service';
import { MonitoringRoutingService } from '../../../../Core/services/monitoring-routing.service';
import { SearchResultsService } from '../../../../Core/services/search-results.service';
import { TopicsService } from '../../../../Core/services/topics.service';
import { RouteType } from '../../../enums/route-type.enum';
import { MonitoringSeriesTypeEnum } from '../../../enums/monitoring-series-type.enum';
import { MonitoringViewModesEnum } from '../../../enums/monitoring-view-modes.enum';
import { InvalidFiltersError } from '../../../errors/invalid-filters.error';
import { Folder } from '../../../models/folder.model';
import { MediaSubscription } from '../../../models/media-subscription.model';
import { mediaSubTypeToMediaTypeMap } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MediaTypeModel } from '../../../models/media-type.model';
import { MentionSearchModel } from '../../../models/mention-search.model';
import { monitoringDateFilterOptions } from '../../../models/monitoring-date-filter-options';
import { getMonitoringDateQueryInputFolderOptions, getMonitoringDateQueryInputTopicOptions } from '../../../models/monitoring-date-query-input-options';
import { MonitoringKeywordGroupModel } from '../../../models/monitoring-keyword-group.model';
import { MonitoringResultErrorsModel } from '../../../models/monitoring-result-errors.model';
import { MonitoringRouteModel } from '../../../models/monitoring-route.model';
import { SentimentModel } from '../../../models/sentiment.model';
import { MentionDeleteDialogComponent } from '../../MentionDeleteDialog/mention-delete-dialog.component';
import { MonitoringBulkActionDialogComponent } from '../../MonitoringBulkActionDialog/monitoring-bulk-action-dialog.component';
import { SaveTopicDialogComponent } from '../../SaveTopicDialog/save-topic-dialog.component';
import { CurrencyConversionService } from '../../../../../Core/Users/services/currency-conversion.service';
import { MentionImportTypesEnum } from '../../../../../Common/MonitorShared/enum/mention-import-types.enum';
import { DateQueryTypeEnum } from '../../../../../api/mentions/enum/date-query-type.enum';
import { AgprComponentStyleEnum } from '../../../../../Common/Basic/enums/social-analysis-types.enum';
import { RelativeTimePipe } from '../../../../../Common/Misc/pipes/RelativeTime/relative-time.pipe';
import { defaultDateTypeOptions, savedSourceDateTypeOptions } from '../../../../../Common/Forms/components/DateRangeSelector/date-range-selector.component';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import {
    MentionWorkbenchDialogComponent
} from '../../../../../Common/Mentions/components/MentionWorkbenchDialog/mention-workbench-dialog.component';
import { WebsocketAwareNeptuneService } from '../../../../../Bootstrap/services/websocket-aware-neptune.service';
import moment from 'moment-timezone';
import { TopicBackfillApi } from '../../../../../api/backfills/topic-backfill.api';
import { TopicBackfillModel } from '../../../models/topic-backfill.model';
import { AttributeModel } from '../../../../../Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '../../../../../Common/Misc/enums/attribute-types.enum';
import { BackfillStatusApiEnum } from '@api/mediaSubscriptions/enums/backfill-status.api.enum';
import { toolbarAnimation } from '../../../../../Common/Misc/animations/toolbar-animation';
import { MentionSearchQueryModel } from '../../../../../Monitor/Shared/models/mention-search-query.model';
import { MentionContentCustomizationEnum } from '../../../../../Common/Mentions/enums/mention-content-customization.enum';
import { BulkActions } from './../../MonitoringBulkActionDialog/bulk-actions';
import { BulkActionConfigModel } from '@Monitor/Shared/models/bulk-action-config.model';
import { MentionSavingLimitExceededDialogComponent } from '../../MentionSavingLimitExceededDialog/mention-saving-limit-exceeded-dialog.component';
import { MentionSavingUsageDashboardComponent } from '../../MentionSavingUsageDashboard/mention-saving-usage-dashboard.component';
import { MonitoringBulkActionTypeEnum } from '@Monitor/Shared/enums/monitoring-bulk-action-type.enum';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { ChipModel } from '../../../../../Common/Forms/models/chip.model';
import { ToolbarStateService } from '@Common/TargetingShared/services/toolbar-state.service';
import { ManageSourceAgentsService } from '../../../../../AutomationAgents/services/manage-source-agents.service';

enum MentionViewOptionsEnum {
    showImages,
    hideImages,
    setCompactMentionSize,
    setNormalMentionSize,
    showMentionIndex,
    hideMentionIndex
}
export enum SearchErrorTypeEnum {
    none,
    unknown,
    filters,
    queryLength,
    apiLimit,
    discoverListNotExist
}

enum Mode {
    folders,
    allSavedCoverage,
    topics,
    other
}

enum SaveOptionEnum {
    folder,
    tag
}

interface SaveChipModel extends ChipModel {
    type: SaveOptionEnum;
    id: number;
    label: string;
    suffix?: string;
}

interface SaveSettings {
    chips?: SaveChipModel[];
}

/** Which agent source a results page stands for, so the Agents button knows what it is managing */
const routeTypeToAgentSourceType: Partial<Record<RouteType, MonitoringSeriesTypeEnum>> = {
    [RouteType.topic]: MonitoringSeriesTypeEnum.topic,
    [RouteType.topicAnalyticsSyndicates]: MonitoringSeriesTypeEnum.topic,
    [RouteType.folder]: MonitoringSeriesTypeEnum.folder,
    [RouteType.folderAnalyticsSyndicates]: MonitoringSeriesTypeEnum.folder,
    [RouteType.subscriptionResults]: MonitoringSeriesTypeEnum.subscription,
    [RouteType.subscriptionAnalyticsSyndicates]: MonitoringSeriesTypeEnum.subscription
};

@Component({
    selector: 'agpr-mention-search-result',
    templateUrl: './mention-search-result.component.html',
    styleUrls: ['./mention-search-result.component.scss'],
    providers: [
        ToolbarStateService,
        {
            provide: 'PaginatorServiceInterface',
            useExisting: MentionSearchService
        }
    ],
    animations: [toolbarAnimation],
    standalone: false
})
export class MentionSearchResultComponent implements OnInit, OnDestroy {

    @Input() showActionBar: boolean = true;
    @Input() header: string;
    @Input() dateFilterOptions: DropdownOptionModel[] = monitoringDateFilterOptions;
    @Input() dateTypeFilterOptions: DropdownOptionModel[] = defaultDateTypeOptions;
    /** Off for a feed opened over a page whose route should not pick its date options, e.g. a panel */
    @Input() dateFilterOptionsFromRoute: boolean = true;
    @Input() showFilterLock: boolean = true;
    @Input() requireCombinedQueryResults: boolean = false;
    @Input() reGetCardHeight: boolean = false;
    @Input() ignoreReset: boolean = false;
    @Input() showMentionViewIcon: boolean = false;
    @Input() isSyndicateMode: boolean = false;
    /**
     * Whether saved mentions are tinted. Off where everything listed is saved already - the folder
     * and saved coverage routes do this from {@link setShowSavedFlag}, and a host with no route of
     * its own, such as the ad hoc report panel, says so here.
     */
    @Input() highlightSavedMentions: boolean = true;
    @Input() forceSyndicateSave: boolean = false;

    @Input() topLabelMention: Mention;
    @Input() topLabel: string;

    @Output() outletClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() contactClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() focusedMentionChange = new EventEmitter<Mention>();
    @Output() mentionCardClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() viewAllSyndicatesClicked: EventEmitter<Mention> = new EventEmitter<Mention>();
    @Output() selectedMentionsChange: EventEmitter<Mention[]> = new EventEmitter<Mention[]>();
    @Output() queryChange: EventEmitter<MentionSearchQueryModel> = new EventEmitter<MentionSearchQueryModel>();
    @Output() dateChanged: EventEmitter<DateQueryModel> = new EventEmitter<DateQueryModel>();

    @ViewChild(MentionListComponent) mentionList: MentionListComponent;
    @ViewChild('filterChipsComponent') filterChipsComponent: FilterChipsComponent;
    @ViewChild('chipsComponent') chipsComponent: ChipContainerComponent;
    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;


    mode: Mode;
    subscriptions: Subscription[] = [];
    relativeTimePipe: RelativeTimePipe;

    actions: DropdownOptionModel[] = BulkActions.map(action => ({
        label: action.name,
        value: action,
        command: () => this.openBulkDialog(action)
    }));

    pagingDialogKey: string = null;

    search: MentionSearchModel;

    subscription: MediaSubscription;

    mentions: Mention[];
    mentionAccountBadges: MentionBadge[] = mentionBadgesSaved;
    mentionMercuryBadges: MentionBadge[] = mentionBadgesAll;

    hasRemainingMentionSavingQuota: boolean = true;
    overlayContainer: Element;

    selectedMentions: Mention[] = [];
    focusedMention: Mention;
    groupDuplicates: boolean = false;
    hasMonitoringAccess: boolean = false;

    mentionCount: number = 0;
    maxMentionCountLimit = {
        [RouteType.topic] : 10000,
        [RouteType.subscriptionResults] : 10000,
        [RouteType.folder] : Infinity,
    };

    currentRoute: MonitoringRouteModel;
    currentMode: Mode;
    routeTypeEnum = RouteType;
    isAccountSearch: boolean = false;


    startDateRange: DateQueryModel;

    errors: MonitoringResultErrorsModel = {
        hasNoSearchName: false
    };

    showImages: boolean = false;
    showImageOption: DropdownOptionModel = new DropdownOptionModel('Monitoring.result.showImages', MentionViewOptionsEnum.showImages);
    hideImageOption: DropdownOptionModel = new DropdownOptionModel('Monitoring.result.hideImages', MentionViewOptionsEnum.hideImages);
    setCompactMentionSizeOption: DropdownOptionModel = new DropdownOptionModel(
        'Monitoring.result.setCompactMentionSize',
        MentionViewOptionsEnum.setCompactMentionSize
    );
    setNormalMentionSizeOption: DropdownOptionModel = new DropdownOptionModel(
        'Monitoring.result.setNormalMentionSize',
        MentionViewOptionsEnum.setNormalMentionSize
    );

    showMentionIndex: boolean = false;
    showMentionIndexOption: DropdownOptionModel = new DropdownOptionModel(
        'Monitoring.result.showMentionIndexOption',
        MentionViewOptionsEnum.showMentionIndex
    );
    hideMentionIndexOption: DropdownOptionModel = new DropdownOptionModel(
        'Monitoring.result.hideMentionIndexOption',
        MentionViewOptionsEnum.hideMentionIndex
    );
    mentionViewOptions: DropdownOptionModel[] = [
        this.showImageOption,
        this.setCompactMentionSizeOption,
        this.hideMentionIndexOption
    ];
    selectedMentionViewOption: DropdownOptionModel;

    errorType: SearchErrorTypeEnum = SearchErrorTypeEnum.none;

    filterError: InvalidFiltersError;
    filtersLocked: boolean = false;
    saveSettingsLocked: boolean = false;
    saveSettingsSessionKey: string = 'agpr-saveSettings';

    readonly saveOptions = [
        new DropdownOptionModel('Monitoring.result.saveToFolder', SaveOptionEnum.folder),
        new DropdownOptionModel('Monitoring.result.saveWithTag', SaveOptionEnum.tag),
    ];

    selectedType: DropdownOptionModel<SaveOptionEnum> = this.saveOptions[0];

    foldersList: Folder[] = [];
    folderList$: Observable<Folder[]>;

    tagsList: TagModel[];
    tagList$: Observable<TagModel[]>;

    selectedFolders: Folder[] = [];
    folderChips: SaveChipModel[] = [];

    selectedTags: TagModel[] = [];
    tagChips: SaveChipModel[] = [];

    chips: SaveChipModel[] = [];

    /** The folder selected in the dropdown as the save target */
    dropdownFolder: Folder = null;


    /** The current folder, if the user is viewing folder content */
    selectedFolder: Folder = null;

    highlightKeywords: KeywordHighlightModel[] = [];

    /** Whether the account is provisioned for automation agents, which the Agents button needs */
    hasAgenticFeature: boolean = false;

    showSavedFlag: boolean = true;
    isLoadingMentions: boolean = true;
    filtersLoading: boolean = false;

    actionBeingPerformed: Subscription;

    searchRange: SearchRangeModel = null;
    saveInProgress: boolean = false;
    refreshing: boolean = false;

    hasInvalidDateRange: boolean = false;
    validDateRangeStartDisplayString: string;
    validDateRangeEndDisplayString: string;
    mentionContent: MentionContentCustomizationEnum  = MentionContentCustomizationEnum.summary;

    availableMediaTypes: MediaTypeEnum[];

    mentionImporterVisible: boolean = false;
    mentionImportTypes: MentionImportTypesEnum[];


    enableActiveButton: boolean = false;
    activateButtonTooltip: string = '';
    isTvEyesEnabled: boolean = false;
    hasProminenceProvision: boolean = false;
    isSyndicateRoute: boolean = false;

    topicDirty: boolean;

    pageHeading: string;
    pageSrHeading: string;

    private backfillAttributesSubject: BehaviorSubject<AttributeModel[][]> = new BehaviorSubject<AttributeModel[][]>([]);
    backfillAttributes$ = this.backfillAttributesSubject.asObservable();

    mentionsLoaded: boolean = false;

    readonly id: string = 'mentionSearchResult';
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly errorTypes = SearchErrorTypeEnum;
    readonly chartTabIndex: number = 1;
    readonly entityTypes = EntityType;
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;
    readonly modeEnum = Mode;
    readonly saveOptionEnum = SaveOptionEnum;
    readonly mediaSubscriptionStatusEnum = MediaSubscriptionStatusEnum;
    readonly viewModesEnum = MonitoringViewModesEnum;
    readonly saveActions = [
        {
            label: this.localizationService.transform('Monitoring.search.saveChanges'),
            command: () => this.saveTopicChanges()
        },
        {
            label: this.localizationService.transform('Monitoring.saveAsNewTopic'),
            command: () => this.saveAsNewTopic()
        }
    ];
    readonly folderAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.folderService.create(folder).pipe(
                    switchMap(id => this.folderService.all$.pipe(
                        map(folders => folders.find(x => x.id === id))
                    )),
                    take(1)
                ).subscribe(newFolder => {
                    this.selectedFolders.push(newFolder);
                    this.selectFolder(newFolder);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];
    readonly tagsAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Tags.createTag',
            action: (searchString: string) => {
                const tagModel = new TagModel();
                tagModel.name = searchString;

                this.tagsService.createTag(tagModel).pipe(
                    switchMap((id) => this.tagsService.getTagById(id))
                ).subscribe(tag => {
                    if (tag) {
                        this.selectedTags.push(tag);
                        this.selectTag(tag);
                    }
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];


    constructor (
        public monitoringRoutingService: MonitoringRoutingService,
        public topicsService: TopicsService,
        public mentionSearchService: MentionSearchService,
        public searchResultsService: SearchResultsService,
        public folderService: FolderService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public tagsService: TagsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public mentionsService: MentionsService,
        public monitorConfigService: MonitorConfigService,
        public route: ActivatedRoute,
        public currentUserStateService: CurrentUserStateService,
        public userPreferencesService: UserPreferencesService,
        public currencyConversionService: CurrencyConversionService,
        public titleService: Title,
        public injector: Injector,
        public websocketEvents: WebsocketAwareNeptuneService,
        public topicBackfillApi: TopicBackfillApi,
        public toolbarStateService: ToolbarStateService,
        public manageSourceAgentsService: ManageSourceAgentsService,
    ) {
        this.relativeTimePipe = new RelativeTimePipe(this.localizationService);
        this.showImages = false;
        this.tagList$ = this.tagsService.all$;
        this.folderList$ = this.folderService.all$;
    }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');
        this.createSubscriptions();
        this.getLockedSaveSettings();

        this.mentionSearchService.setPageNumber(1);
        this.mentionSearchService.setRequireCombinedQueryResults(this.requireCombinedQueryResults);

        // handle loading filters from query params first
        if (this.route.snapshot.data.filters) {
            this.filtersLoading = true;
            this.subscriptions.push(
                combineLatest([
                    this.currentUserStateService.getPreferences().pipe(filter(x => !!x)),
                    this.currencyConversionService.getCurrencyRates().pipe(filter(x => !!x)),
                ]).pipe(
                    take(1),
                    switchMap(() => {
                        this.search = new MentionSearchModel();
                        this.search.searchCriteria.query = this.route.snapshot.data.filters.query;
                        this.mentionSearchService.populateFiltersFromSearchQuery(this.route.snapshot.data.filters.query, true, true);

                        // Only apply when showSimilar filter is explicitly false
                        const showSimilar = this.route.snapshot.data.filters?.showSimilar;
                        if (showSimilar === false) {
                            this.search.searchCriteria.showSimilar = false;
                            this.mentionSearchService.setDuplicateFilter(DuplicatesStatusEnum.hide);
                        }

                        this.mentionSearchService.applyFilters(false);
                        this.mentionSearchService.search(true, true);

                        if (this.search.searchCriteria.query.sentiments.length <= 0) {
                            this.search.searchCriteria.query.sentiments = SentimentModel.getAll();
                        }

                        if (this.search.searchCriteria.query.keywordGroups.length <= 0) {
                            this.search.searchCriteria.query.keywordGroups.push(new MonitoringKeywordGroupModel());
                        }

                        if (this.search.searchCriteria.query.includedMediaTypes.length <= 0 && this.availableMediaTypes?.length >= 0) {
                            this.search.searchCriteria.query.includedMediaTypes = MediaTypeModel.getAllMediaTypes()
                                .filter(mediaType => this.availableMediaTypes.indexOf(mediaType.id) !== -1);
                        }

                        this.topicsService.setCurrentResource(this.search);

                        return this.mentionSearchService.getFlatFilters().pipe(
                            map(filters => filters.some(filterType => filterType.loading)),
                            filter(stillLoading => !stillLoading),
                            take(1)
                        );
                    })
                ).subscribe(() => {
                    this.filtersLoading = false;
                })
            );
        }

        this.filtersLocked = this.searchResultsService.filtersLocked();
        this.searchResultsService.setIsViewModeChange(false);

        this.updateMentionViewOptions();
    }

    /**
     * Handles state change in duplicate mention filter
     */
    groupDuplicatesUpdate(value: DuplicatesStatusEnum): void {
        switch (value) {
            case DuplicatesStatusEnum.show:
                this.groupDuplicates = false;
                break;
            case DuplicatesStatusEnum.group:
                this.groupDuplicates = true;
                break;
            case DuplicatesStatusEnum.hide:
                this.groupDuplicates = false;
        }
    }

    /**
     * Sets the provided mention as the focused mention
    */
    setFocusedMention(mention: Mention): void {
        this.mentionsService.setFocusedMention(mention);
        this.focusedMentionChange.emit(mention);
    }

    /**
     * Creates the subscriptions this component relies on
    */
    public createSubscriptions(): void {
        this.subscriptions.push(
            this.currentUserStateService.getAgenticFeatureEnabled().subscribe(enabled => this.hasAgenticFeature = enabled),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(
                permissions => {
                    // remove the renormalize action for non Adv. Monitoring accounts, if it's present
                    if (!permissions?.includes(userPermissionsEnum.advancedMonitoringManager)) {
                        this.actions = remove(this.actions, (action) => {
                            return action.value?.type !== MonitoringBulkActionTypeEnum.renormalize;
                        });
                    }
                    if ((!permissions?.includes(userPermissionsEnum.accountManager)
                        && !permissions?.includes(userPermissionsEnum.accountsReadOnlyManager))
                        || !permissions?.includes(userPermissionsEnum.copyMentionsToAccount)) {
                        this.actions = remove(this.actions, (action) => {
                            return action.value?.type !== MonitoringBulkActionTypeEnum.copyMentionsToAccount;
                        });
                    }
                }
            ),
            this.folderList$.subscribe(
                folders => {
                    this.foldersList = folders || [];
                    this.getLockedSaveSettings();
                }
            ),
            this.folderService.currentResource$.subscribe(
                folder => this.onUpdateSelectedFolder(folder)
            ),
            this.mentionsService.getMentions().subscribe(
                mentions => {
                    this.mentionsLoaded = true;
                    if (!mentions) {
                        this.mentions = [];

                        return;
                    }

                    let isRefresh = true;
                    if (mentions && this.mentions && mentions.length === this.mentions.length) {
                        for (let i = 0, l = mentions.length; i < l; i++) {
                            const oldMention = this.mentions[i];
                            const newMention = mentions[i];
                            if (oldMention.id !== newMention.id || oldMention.mentionId !== newMention.mentionId) {
                                isRefresh = false;
                                break;
                            }
                        }
                    } else {
                        isRefresh = false;
                    }

                    this.mentions = mentions.slice();

                    if (!isRefresh) {
                        this.selectNone();
                        if (this.scrollPanel) {
                            this.scrollPanel.scrollTo(0, 0, false); // scroll to top
                        }
                    } else {
                        this.selectedMentions = this.mentions.filter(mention => {
                            return !!this.selectedMentions.find(selectedMention => {
                                return (selectedMention.id && selectedMention.id === mention.id) ||
                                    (selectedMention.mentionId && selectedMention.mentionId === mention.mentionId);
                            });
                        });
                        this.selectedMentionsChange.emit(this.selectedMentions);
                    }
                }
            ),
            this.mentionsService.getMentionsCount().subscribe(
                mentionCount => {
                    if (!mentionCount) {
                        this.mentionCount = 0;
                        return;
                    }
                    this.mentionCount = mentionCount;
                }
            ),
            this.topicsService.getFocusedTopic().pipe(
                tap(topic => {
                    if (!topic) {
                        return;
                    }

                    this.search = topic;

                    this.isAccountSearch = false;
                    if (topic.id) {
                        this.titleService.setTitle(
                            this.localizationService.transform('Monitoring.topics.browserTitle.name', {name: topic.name})
                        );
                        this.pageHeading = topic.name;
                        this.pageSrHeading = 'Monitor.primaryHeading.topic';
                    }
                    if (this.search.searchCriteria) {
                        this.isAccountSearch = this.search.searchCriteria.isAccountSearch;
                    }
                }),
                switchMap(() => {
                    if (this.search?.id) {
                        return this.topicBackfillApi.getAllWithCount(this.search.id).pipe(
                            tap(backfills => {
                                const topicBackfills = backfills?.map(backfillApiModel => new TopicBackfillModel(backfillApiModel));
                                this.setTopicBackfillAttributes(topicBackfills);
                            })
                        );
                    } else {
                        return of(null);
                    }
                })
            ).subscribe(),
            this.topicsService.getDirtyFlag().subscribe(isDirty => this.topicDirty = isDirty),
            this.mentionSearchService.getHighlightKeywords().subscribe(
                highlightKeywords => {
                    this.highlightKeywords = highlightKeywords;
                }
            ),
            this.mentionSearchService.getSearchState().subscribe(
                searchState => {
                    this.filterError = null;
                    this.errorType = SearchErrorTypeEnum.none;
                    this.isLoadingMentions = false;

                    switch (searchState.type) {
                        case SearchStatesEnum.loading:
                            this.isLoadingMentions = true;
                            break;
                        case SearchStatesEnum.error:
                            const errorState = searchState as SearchStateErrorModel;
                            if (errorState.error instanceof InvalidFiltersError) {
                                this.errorType = SearchErrorTypeEnum.filters;
                                this.filterError = errorState.error;
                            } else if (errorState.error instanceof SearchError) {
                                switch (errorState.error.type) {
                                    case SearchErrorsEnum.queryTooLong:
                                        this.errorType = SearchErrorTypeEnum.queryLength;
                                        break;
                                    case SearchErrorsEnum.outsideApiLimit:
                                        this.errorType = SearchErrorTypeEnum.apiLimit;
                                        break;
                                    case SearchErrorsEnum.discoverListNotExist:
                                        this.errorType = SearchErrorTypeEnum.discoverListNotExist;
                                        break;
                                    case SearchErrorsEnum.unknown:
                                    default:
                                        this.errorType = SearchErrorTypeEnum.unknown;
                                        break;
                                }
                            } else {
                                this.errorType = SearchErrorTypeEnum.unknown;
                            }
                            break;
                        default:
                    }
                }
            ),
            // Merge two sources of display settings:
            // 1. The current display settings from the SearchResultsService.
            // 2. User preference display settings based on the current route (topics, folders, allSavedCoverage).
            merge (
                this.searchResultsService.getCurrentDisplaySettings(),
                this.monitoringRoutingService.getCurrentRoute().pipe(
                    map(route => this.getModeFromRoute(route)),
                    rxjsFilter(mode => mode !== Mode.other),
                    distinctUntilChanged(),
                    tap(mode => this.mode = mode),
                    switchMap(mode => {
                        return (mode !== null && mode !== undefined ? this.getUserPreferencesFromRoute(mode) : of(null));
                    }))
            ).subscribe(displaySettings => {
                this.setDisplaySettingsFromUserPrefs(displaySettings);
            }),
            this.monitoringRoutingService.getCurrentRoute().subscribe(route => {
                this.currentRoute = route;

                this.setActiveDateFilterOptions();
                this.setShowSavedFlag();
                this.updateMentionViewOptions();
                this.resetSaveSettings();

                if (!this.mentionsLoaded || !this.ignoreReset) {
                    this.resetLoadedData();
                } else {
                    this.isLoadingMentions = false;
                }

                // Null off the Monitoring routes - this component is reused by the agent feed, and
                // throwing here abandons the rest of createSubscriptions()
                this.isSyndicateRoute = (this.currentRoute?.type === RouteType.topicAnalyticsSyndicates) ||
                    (this.currentRoute?.type === RouteType.adHocAnalyticsSyndicates) ||
                    (this.currentRoute?.type === RouteType.folderAnalyticsSyndicates) ||
                    (this.currentRoute?.type === RouteType.subscriptionAnalyticsSyndicates) ||
                    (this.currentRoute?.type === RouteType.allSavedCoverageAnalyticsSyndicates);
            }),
            this.mentionSearchService.getFilters().subscribe(
                filters => {
                    if (!filters) {
                        return;
                    }
                    const duplicatesFilter = filters.find(filterFind => filterFind instanceof FilterTypeDuplicatesModel);

                    if (!duplicatesFilter) {
                        return;
                    }

                    this.groupDuplicatesUpdate(duplicatesFilter.getValue());
                }
            ),
            this.tagList$.subscribe(
                tags => {
                    this.tagsList = tags || [];
                    this.getLockedSaveSettings();
                }
            ),
            this.mediaSubscriptionService.currentResource$.subscribe(
                subscription => {
                    this.subscription = subscription;

                    if ((this.subscription?.type !== MediaSubscriptionMediaTypeEnum.broadcast) &&
                        (this.subscription?.type !== MediaSubscriptionMediaTypeEnum.podcast)
                    ) {
                        this.enableActiveButton = !!this.availableMediaTypes?.
                            find(type => type === mediaSubTypeToMediaTypeMap.get(subscription?.type));
                    } else {
                        this.enableActiveButton = false;
                        this.activateButtonTooltip = 'MediaSubscriptions.activate.disabled';
                    }
                    this.activateButtonTooltip = (this.enableActiveButton) ? '' : 'MediaSubscriptions.activate.disabled';
                    if (subscription) {
                        this.titleService.setTitle(
                            this.localizationService.transform('Monitoring.subscriptions.browserTitle.name', {name: subscription.name})
                        );
                        this.pageHeading = subscription.name;
                        this.pageSrHeading = 'Monitor.primaryHeading.subscription';

                        if (subscription.backfills?.length) {
                            this.setSearchRangeFromBackfill();
                        } else {
                            this.backfillAttributesSubject.next([]);
                        }
                    }
                }
            ),
            this.monitorConfigService.getTvEyesEnabled().subscribe(enabled => {
                this.isTvEyesEnabled = enabled;
                this.activateButtonTooltip = (this.enableActiveButton) ? '' : 'MediaSubscriptions.activate.disabled';
            }),
            combineLatest([
                this.currentUserStateService.getPreferences().pipe(
                    filter(x => !!x),
                    map(pref => pref.timeZone)
                ),
                this.mentionSearchService.getDateQuery().pipe(
                    tap(dateQuery => this.startDateRange = dateQuery)
                ),
                this.monitorConfigService.getSearchRange().pipe(
                    tap(searchRange => {
                        this.searchRange = searchRange;
                        if (this.subscription?.backfills?.length) {
                            this.setSearchRangeFromBackfill();
                        }
                    }),
                    filter(x => !!x)
                ),
                this.monitoringRoutingService.getCurrentRoute().pipe(
                    filter(x => !!x),
                )
            ]).subscribe(([userPreferenceTimezone, startDateRange, searchRange, route]) => {
                this.hasInvalidDateRange = startDateRange.absoluteRange && startDateRange.absoluteRange.start < searchRange.from
                    && startDateRange.absoluteRange.end > searchRange.from
                    && route.type === RouteType.topic;
                if (this.hasInvalidDateRange) {
                    this.validDateRangeStartDisplayString = moment(searchRange.from).tz(userPreferenceTimezone).format('YYYY/MM/DD');
                    this.validDateRangeEndDisplayString = moment(startDateRange.absoluteRange.end).tz(userPreferenceTimezone).format('YYYY/MM/DD');
                }
            }),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(availableMediaTypes => {
                this.availableMediaTypes = availableMediaTypes;
                if ((this.subscription?.type !== MediaSubscriptionMediaTypeEnum.broadcast) &&
                    (this.subscription?.type !== MediaSubscriptionMediaTypeEnum.podcast)
                ) {
                    this.enableActiveButton = !!this.availableMediaTypes?.
                        find(type => type === mediaSubTypeToMediaTypeMap.get(this.subscription?.type));
                    this.activateButtonTooltip = this.enableActiveButton ? '' : 'MediaSubscriptions.activate.disabled';
                } else {
                    this.enableActiveButton = false;
                    this.activateButtonTooltip = 'MediaSubscriptions.activate.disabled';
                }
                if (this.search && this.route.snapshot.data.filters) {
                    this.search.searchCriteria.query.includedMediaTypes = MediaTypeModel.getAllMediaTypes()
                        .filter(mediaType => this.availableMediaTypes.indexOf(mediaType.id) !== -1);
                }
            }),
            this.monitorConfigService.getProminenceProvision().subscribe(groupsLimit => {
                this.hasProminenceProvision = groupsLimit > 0;
            }),
            this.monitorConfigService.getMentionImportTypes().subscribe(mentionImportTypes => {
                this.mentionImportTypes = mentionImportTypes;
            }),
            this.mentionsService.getFocusedMention().subscribe(
                mention => {
                    this.focusedMention = mention;
                }
            ),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(message => !!message && (message.resourceType === 'mentions' && message.slug?.includes('tveyes/archiveorder/completed'))),
                map(message => message.slug.split('mentionId=')[1]),
            ).subscribe(mentionId => {
                if (mentionId.trim().length > 0) {
                    this.mentionsService.refetchMentions([mentionId]);
                }
            }),
            this.userPreferencesService.getUserAppPreferences()
                .subscribe(prefs => this.toolbarStateService.setToolbarAnimationDisabled(!prefs.generalPrefs.accessibility.collapseToolbars)),
            this.mentionSearchService.getCombinedQuery().subscribe(combinedQuery => this.queryChange.emit(combinedQuery)),
            this.monitorConfigService.getMentionSavingQuota().subscribe(quota => {
                this.hasRemainingMentionSavingQuota = quota?.savingQuota === -1 || quota?.savingQuotaRemaining > 0;
            }),
            this.currentUserStateService.hasMonitoringAccess()
                .pipe(filter(x => !!x))
                .subscribe(hasMonitoringAccess => this.hasMonitoringAccess = hasMonitoringAccess),
        );

        this.mentionSearchService.registerPagingValidator(this.pagingValidator.bind(this));
    }

    /**
     * Resets the loaded data that a user sees when changing routes
     */
    private resetLoadedData(): void {
        if (!this.ignoreReset) {
            this.mentions = [];
            this.mentionCount = 0;
            this.setFocusedMention(null);
            this.updateMentionViewOptions();
        }
    }

    /**
     * updates the date range and re-runs the search
     *
     * @param dateRange
     *
     * @returns void
     */
    dateRangeChanged(dateRange: DateQueryModel): void {
        this.mentionSearchService.setDateQuery(dateRange);
        this.mentionSearchService.setPageNumber(1);
        this.mentionSearchService.search(true, true);
        this.dateChanged.emit(dateRange);
    }

    /**
     * Respond to updates to the selected folder
    */
    private onUpdateSelectedFolder(folder: Folder): void {
        if (folder) {
            this.selectedFolder = folder;
            this.titleService.setTitle(
                this.localizationService.transform('Monitoring.savedCoverage.browserTitle.name', {name: folder.name})
            );
            this.pageHeading = folder.name;
            this.pageSrHeading = 'Monitor.primaryHeading.folder';
        } else {
            this.selectedFolder = null;
            this.pageHeading = null;
        }
    }

    saveTopic(): void {
        this.coreDialogService.open(
            SaveTopicDialogComponent,
            {
                inputs: {
                    search: this.search,
                    saveAsNew: false
                }
            }
        );
    }

    saveAsNewTopic(): void {
        this.coreDialogService.open(
            SaveTopicDialogComponent,
            {
                inputs: {
                    search: this.search,
                    saveAsNew: true,
                    currentGroup: this.search.groupId
                }
            }
        );
    }

    /**
     * Opens the panel listing the automation agents watching the topic, folder or subscription
     * these results belong to. The filters applied here go with it, so an agent added from it
     * watches what is on screen rather than the whole source.
     */
    openAgents(): void {
        const sourceType = routeTypeToAgentSourceType[this.currentRoute?.type];

        this.manageSourceAgentsService.open(
            sourceType,
            this.currentRoute?.value,
            this.mentionSearchService.generateFilterSearchCriteria()
        );
    }

    openBulkDialog(action?: BulkActionConfigModel): void {
        let search = this.search?.clone(false);
        if (this.subscription) {
            search = this.subscription.toMentionSearchModel();
        }

        const bulkActionData: DialogIoModel = {
            inputs: {
                mentionIds: this.getSelectedMentionIds(),
                mercuryIds: this.getSelectedMercuryIds(),
                initialFilters: this.mentionSearchService.generateFilterSearchCriteria(),
                route: this.currentRoute,
                dateQuery: this.startDateRange,
                search: search,
                mentions: this.selectedMentions,
                selectedActionOption: this.actions.find(x => x.label === action?.name),
            },
            outputs: {
                changes: () => {
                    // in case mention counts have changed
                    this.folderService.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                    ).subscribe();
                    this.mentionSearchService.search(true, true);
                }
            }
        };
        this.coreDialogService.open(MonitoringBulkActionDialogComponent, bulkActionData);
    }

    gotoUpdateSubscription(): void {
        this.monitoringRoutingService.gotoUpdateMediaSubscription(+this.currentRoute.value);
    }


    openDeleteMentionsDialog(): void {
        const io: DialogIoModel = {
            inputs: {
                id: 'monitoringResult.deleteMentionDialog',
                dialogHeader: 'Monitoring.result.deleteMentionsHeader',
                dialogMessage: 'Monitoring.result.deleteMentionsHeadline',
                dialogConfirmMsg: 'Monitoring.result.deleteMentionsConfirmation',
                folderNames$: this.folderService.getListOfFoldersMentionsFoundIn(this.selectedMentions),
                selectedMentions: this.selectedMentions,
            },
            outputs: {
                dialogClose: (result: { closeAction: boolean; reason?: string }) => {
                    if (result.closeAction) {
                        // remove selected mentions from the current user account
                        this.mentionsService.removeMentionsFromAccount(
                            this.selectedMentions,
                            undefined,
                            undefined,
                            result.reason
                        ).pipe(
                            switchMap(() =>
                                this.folderService.refreshAllManual().pipe(
                                    filter(x => !!x),
                                    take(1),
                                )
                            )
                        ).subscribe(() => {
                            this.mentionSearchService.search(true, true);
                        });
                    }
                }
            }
        };

        this.coreDialogService.open(MentionDeleteDialogComponent, io);
    }

    /**
     * Refreshes the results.
     */
    public refresh(): void {
        this.refreshing = true;
        this.mentionSearchService.search(true, true).subscribe(result => this.refreshing = false);
    }

    /**
     * Selects all mentions on the page
     *
     * @returns void
     */
    public selectAll(): void {
        this.selectedMentions = this.mentions.slice();
        this.selectedMentionsChange.emit(this.selectedMentions);
    }

    /**
     * Deselects all mentions on the page
     *
     * @returns void
     */
    public selectNone(): void {
        this.selectedMentions = [];
        this.selectedMentionsChange.emit(this.selectedMentions);
    }

    public toggleSelectAll(): void {
        if (this.mentions.length === this.selectedMentions.length) {
            this.selectNone();
        } else {
            this.selectAll();
        }
    }

    /**
     * Goes to the update workflow for the current search
    */
    public updateSavedSearch(): void {
        this.topicsService.openEditTopicPanel(this.search);
    }

    /**
     * Saves changes made to a topic
     */
    public saveTopicChanges() {
        if (this.saveInProgress) {
            return;
        }

        this.saveInProgress = true;
        this.topicsService.updateTopic(this.search).subscribe(
            () => {
                this.saveInProgress = false;
            }
        );
    }

    /**
     * Routes to the update search workflow
    */
    public updateSearch(): void {
        this.topicsService.openEditTopicPanel(this.search);
    }

    /**
     * Gets the ids for all of the currently selected mentions
     *
     * @returns A list of mentions
     */
    private getSelectedMentionIds(): string[] {
        return this.selectedMentions.map(mention => mention.mentionId).filter(x => !!x);
    }

    /**
     * Gets the ids for all of the currently selected mentions
     *
     * @returns A list of mentions
     */
    private getSelectedMercuryIds(): string[] {
        return this.selectedMentions.map(mention => mention.id).filter(x => !!x);
    }

    /**
     * Stores an action that is being performed so that we can prevent more actions until the current one is complete
     */
    private setActionBeingPerformed(action: Observable<any>): void {
        this.actionBeingPerformed = action.subscribe({
            next: () => {
                if (this.actionBeingPerformed) {
                    this.actionBeingPerformed.unsubscribe();
                    this.actionBeingPerformed = null;
                }
            },
            error: () => {
                if (this.actionBeingPerformed) {
                    this.actionBeingPerformed.unsubscribe();
                }
                this.actionBeingPerformed = null;
            }
        });
    }

    /**
     * Saves the selected mentions to the currently selected folder
     */
    public saveMentionsToFolder(): void {
        if (this.actionBeingPerformed) {
            return;
        }

        const request = this.folderService.saveMentionsToFolder(
            this.selectedMentions,
            this.selectedFolders,
            !this.selectedTags?.length,
            this.subscription ? this.subscription.toMentionSearchModel() : this.search,
            this.selectedTags,
            this.forceSyndicateSave
        );

        this.setActionBeingPerformed(request);
    }

    /**
     * Saves the selected mentions to the current account
     *
     * TODO: should be moved somewhere else, the component should not talk directly to the API
     *
     * @returns void
     */
    public saveMentionsToAccount(): void {
        if (this.actionBeingPerformed) {
            return;
        }

        const request = this.mentionsService.saveMentionsToAccount(
            this.selectedMentions,
            !this.selectedTags?.length,
            this.subscription ? this.subscription.toMentionSearchModel() : this.search,
            this.selectedTags,
            this.forceSyndicateSave
        );

        this.setActionBeingPerformed(request);
    }

    unlockMention(mention: Mention): void {
        if (this.actionBeingPerformed) {
            return;
        }

        const request = this.mentionsService.refetchMentions([mention.id], false, true);

        this.setActionBeingPerformed(request);
    }

    /**
     * Validates that paging can be performed at this time
     * @NOTE if mentions are selected, then let the user verify/decide what to do
     */
    public pagingValidator(): Observable<boolean> {
        if (this.selectedMentions.length > 0 && !this.pagingDialogKey) {
            const pagingValidationSubject = new Subject<boolean>();

            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Monitoring.result.navigationWarningHeader',
                    messageHtml: 'Monitoring.result.navigationWarningMessageHtml'
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        this.pagingDialogKey = null;
                        pagingValidationSubject.next(confirm);
                    }
                }
            };
            this.pagingDialogKey = this.coreDialogService.open(ConfirmDialogComponent, io);

            return pagingValidationSubject;
        }
        return of(true);
    }

    /**
     * Categorizes save action to either save to folder or to account
     *
     * @returns void
     */
    public saveSelectedMentions(): void {
        if (this.mode === Mode.topics && !this.hasRemainingMentionSavingQuota) {
            this.openMentionSavingLimitExceededDialog();
            return;
        }
        if (this.selectedFolders?.length > 0) {
            this.saveMentionsToFolder();
        } else {
            this.saveMentionsToAccount();
        }
    }

    public openMentionSavingLimitExceededDialog(): void {
        this.coreDialogService.open(MentionSavingLimitExceededDialogComponent, {
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if(confirmed) {
                        this.openMentionSavingUsageDashboard();
                    }
                }
            }
        });
    }

    public openMentionSavingUsageDashboard(): void {
        this.coreDialogService.open(MentionSavingUsageDashboardComponent, {
            inputs: {
                overlayContainer: this.overlayContainer,
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            }
        });
    }

    /**
     * Removes selected mentions from folder
    */
    public removeMentionsFromFolder(): void {
        const mentions: Mention[] = this.selectedMentions;
        const folder: Folder = this.selectedFolder;

        this.folderService.removeMentionsFromFolder(mentions, folder).subscribe();
    }

    /**
     * Sets whether or not to show an error that
     * the search name must be filled out
    */
    public toggleSearchNameError(isResetOnly: boolean = false): void {
        const hasSearchName: boolean = this.search.hasName();

        if (isResetOnly && hasSearchName) {
            this.errors.hasNoSearchName = false;
        } else if (!isResetOnly) {
            this.errors.hasNoSearchName = !hasSearchName;
        }
    }

    /**
     * Sets whether or not to show an error that
     * the subscription name must be filled out
     */
    public toggleSubscriptionNameError(isResetOnly: boolean = false): void {
        const hasSubscriptionName: boolean = this.subscription && this.subscription.name && this.subscription.name.trim().length >= 1;

        if (isResetOnly && hasSubscriptionName) {
            this.errors.hasNoSearchName = false;
        } else if (!isResetOnly) {
            this.errors.hasNoSearchName = !hasSubscriptionName;
        }
    }

    private setShowSavedFlag(): void {
        if (this.currentRoute) {
            // Don't show the flagged mention in folder or all saved mentions views
            this.showSavedFlag = !(this.currentRoute.type === RouteType.folder ||
                this.currentRoute.type === RouteType.mentions ||
                this.currentRoute.type === RouteType.folderAnalyticsSyndicates);
        }
    }

    public mentionTrackBy(index, mention) {
        return mention ? mention.id : null;
    }

    /**
     * Based upon the current route, set starting values for search date range
     */
    private setActiveDateFilterOptions() {
        if (!this.currentRoute || !this.dateFilterOptionsFromRoute) {
            return;
        }

        switch (this.currentRoute.type) {
            case RouteType.mentions:
            case RouteType.folder:
            case RouteType.folderAnalyticsSyndicates:
            case RouteType.allSavedCoverageAnalyticsSyndicates:
                this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
                this.dateTypeFilterOptions = savedSourceDateTypeOptions;
                break;
            case RouteType.results:
            case RouteType.adHocAnalyticsSyndicates:
                if (this.search && this.search.searchCriteria && this.search.searchCriteria.isAccountSearch) {
                    this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
                    this.dateTypeFilterOptions = savedSourceDateTypeOptions;
                    this.isAccountSearch = true;
                } else {
                    this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
                }
                break;
            case RouteType.report:
            case null:
                // will use @Input to set dateFilterOptions for report and dashboard module
                break;
            default:
                this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
                break;
        }
    }

    public editSelectedMention(): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                mention: this.focusedMention
            },

            outputs: {
                refreshSearch: (refreshSearch: boolean) => {
                    if (refreshSearch) {
                        this.mentionSearchService.search(true, true);
                    }
                }
            }
        };

        this.coreDialogService.open(MentionWorkbenchDialogComponent, ioModel, this.injector);
    }

    public selectFolder(folder: Folder): void {
        this.chips = [
            ...this.chips,
            {
                type: SaveOptionEnum.folder,
                id: folder.id,
                label: folder.name,
                suffix: 'Monitoring.result.saveSettings.chipSuffix.folder'
            }
        ];
        this.updateLockedSaveSettings();
    }

    /**
     * Selects a tag to be used when saving mentions
     */
    public async selectTag(tag: TagModel): Promise<void> {
        this.chips = [
            ...this.chips,
            {
                type: SaveOptionEnum.tag,
                id: tag.id,
                label: tag.name,
                suffix: 'Monitoring.result.saveSettings.chipSuffix.tag'
            }
        ];
        this.updateLockedSaveSettings();
    }

    /**
     * Removes a folder/tag chip from the list to be used when saving mentions
     * @param index Index of the chip being removed
     */
    public removeChip(index: number): void {
        const chip = this.chips[index];
        if (chip.type === SaveOptionEnum.folder) {
            this.selectedFolders = this.selectedFolders.filter(folder => folder.id !== chip.id);
        }
        if (chip.type === SaveOptionEnum.tag) {
            this.selectedTags = this.selectedTags.filter(tag => tag.id !== chip.id);
        }
        this.chips.splice(index, 1);
        this.chips = [...this.chips];
        this.updateLockedSaveSettings();
    }

    /**
     * Updates the session storage for the save settings
     */
    updateLockedSaveSettings(): void {
        if (this.saveSettingsLocked) {
            const settings: SaveSettings = {
                chips: this.chips.map(chip => {
                    return {
                        type: chip.type,
                        id: chip.id,
                        label: chip.label
                    };
                })
            };
            sessionStorage.setItem(this.saveSettingsSessionKey, JSON.stringify(settings));
        } else {
            sessionStorage.removeItem(this.saveSettingsSessionKey);
        }
    }

    /**
     * Gets the locked save settings from the session: folders and tags for saving mentions.
     */
    private getLockedSaveSettings() {
        const settingsString: string = sessionStorage.getItem(this.saveSettingsSessionKey);

        if (settingsString) {
            const settings: SaveSettings = JSON.parse(settingsString);
            this.saveSettingsLocked = true;

            const chips = [];
            const selectedFolders = [];
            const selectedTags = [];
            // Note: This function may be called before this.foldersList/this.tagsList is loaded, so it should be called again when they are loaded.
            settings.chips.forEach(chip => {
                if (chip.type === SaveOptionEnum.folder) {
                    const folder = this.foldersList.find(item => item.id === chip.id);
                    if (folder) {
                        selectedFolders.push(folder);
                        chips.push({
                            type: SaveOptionEnum.folder,
                            id: folder.id,
                            label: folder.name,
                            suffix: 'Monitoring.result.saveSettings.chipSuffix.folder'
                        });
                    }
                }
                if (chip.type === SaveOptionEnum.tag) {
                    if (this.tagsList) {
                        const tag = this.tagsList.find(item => item.id === chip.id);
                        if (tag) {
                            selectedTags.push(tag);
                            chips.push({
                                type: SaveOptionEnum.tag,
                                id: tag.id,
                                label: tag.name,
                                suffix: 'Monitoring.result.saveSettings.chipSuffix.tag'
                            });
                        }
                    }
                }
            });

            this.chips = chips;
            this.selectedFolders = selectedFolders;
            this.selectedTags = selectedTags;
        }
    }

    private getDefaultDateQuery(): DateQueryModel {
        if (this.currentRoute.type === RouteType.folder || this.currentRoute.type === RouteType.mentions) {
            return DateQueryModel.getAllQuery();
        }
        return DateQueryModel.getLast24HoursQuery();
    }

    /**
     * Clears the current filters
     */
    public clearFilters(doSearch: boolean = false): void {
        this.filterChipsComponent?.chipContainer.chipPopover.hide();
        this.mentionSearchService.clearFilters();
        this.filtersLocked = false;
        this.searchResultsService.setFiltersLocked(false);

        if (doSearch) {
            // refresh primary facets as well because this also resets the date query
            this.mentionSearchService.search(true, true);
        }
    }

    toggleFilterLock() {
        this.filtersLocked = !this.filtersLocked;
        this.searchResultsService.setFiltersLocked(this.filtersLocked);
    }

    /**
     * Resets the save settings
     * @param force Forces a clear even if locked
     */
    resetSaveSettings(force: boolean = false): void {
        if (this.saveSettingsLocked && !force) {
            return;
        }

        this.selectedFolders = [];
        this.selectedTags = [];
        this.chips = [];
    }

    /**
     * Clear the current Saved Settings
     */
    clearSavedSettings() {
        this.chipsComponent.chipPopover.hide();
        this.resetSaveSettings(true);
        this.saveSettingsLocked = false;
        this.updateLockedSaveSettings();
    }

    /**
     * Toggle the lock state of the save settings
     */
    toggleSaveSettingsLock() {
        this.saveSettingsLocked = !this.saveSettingsLocked;
        this.updateLockedSaveSettings();
    }

    selectMentionViewOption(): void {
        const opt = this.selectedMentionViewOption;
        switch (opt.value) {
            case MentionViewOptionsEnum.showImages:
                this.showImages = true;
                break;
            case MentionViewOptionsEnum.hideImages:
                this.showImages = false;
                break;
            case MentionViewOptionsEnum.setCompactMentionSize:
                this.searchResultsService.mentionsCompact = true;
                break;
            case MentionViewOptionsEnum.setNormalMentionSize:
                this.searchResultsService.mentionsCompact = false;
                break;
            case MentionViewOptionsEnum.showMentionIndex:
                this.showMentionIndex = true;
                break;
            case MentionViewOptionsEnum.hideMentionIndex:
                this.showMentionIndex = false;
                break;
        }
        this.updateMentionViewOptions();
    }

    updateMentionViewOptions(): void {
        this.mentionViewOptions = [
            this.showImages ? this.hideImageOption : this.showImageOption,
            this.searchResultsService.mentionsCompact ? this.setNormalMentionSizeOption : this.setCompactMentionSizeOption,
            this.showMentionIndex ? this.hideMentionIndexOption : this.showMentionIndexOption
        ];
        this.selectedMentionViewOption = null;
    }

    searchNewContent() {
        if (!this.search.searchCriteria.query.isUnsavedSearchValid()) {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Monitoring.result.savedCoverageSearch.invalidNewSearch.heading',
                    message: 'Monitoring.result.savedCoverageSearch.invalidNewSearch.text',
                    okLabel: 'Monitoring.result.savedCoverageSearch.invalidNewSearch.updateSearch',
                    id: 'InvalidNewSearchPopup'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.convertToUnsavedSearch();
                            this.monitoringRoutingService.gotoUpdateSearch();
                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        } else {
            this.convertToUnsavedSearch();
            this.mentionSearchService.setSearchCriteria(this.search.searchCriteria);
            this.setActiveDateFilterOptions();
            this.mentionSearchService.clearSearch();

            this.dateRangeChanged(this.startDateRange);
        }
    }

    convertToUnsavedSearch(): void {
        this.search.searchCriteria.isAccountSearch = false;
        this.search.searchCriteria.query.includedMediaTypes = this.search.searchCriteria.query.includedMediaTypes.filter(media => {
            return !media.isSavedSearch;
        });
        if (
            DateQueryModel.equals(this.startDateRange, DateQueryModel.getAllQuery()) ||
            (
                this.startDateRange.type === DateQueryTypeEnum.ABSOLUTE &&
                this.startDateRange.absoluteRange.start <= this.searchRange.from
            )
        ) {
            this.startDateRange = DateQueryModel.getLast24HoursQuery();
            this.hasInvalidDateRange = false;
            this.validDateRangeStartDisplayString = '';
            this.validDateRangeEndDisplayString = '';
        }

        this.search.searchCriteria.query.folder.include = [];
        this.search.searchCriteria.query.folder.exclude = [];
        this.search.searchCriteria.query.tag.include = [];
        this.search.searchCriteria.query.tag.exclude = [];

        this.isAccountSearch = false;
    }

    private setSearchRangeFromBackfill() {
        let earliestDate: Date;
        let latestDate: Date;
        this.subscription.backfills.forEach(backfill => {
            if (!earliestDate) {
                earliestDate = backfill.fromDate;
            }
            if (!latestDate) {
                latestDate = backfill.toDate;
            }
            earliestDate = earliestDate < backfill.fromDate
                ? earliestDate
                : backfill.fromDate;

            latestDate = latestDate > backfill.toDate
                ? latestDate
                : backfill.toDate;
        });

        this.searchRange = new SearchRangeModel();
        this.searchRange.from = earliestDate;
        this.searchRange.to = latestDate;
        this.setSubscriptionBackfillAttributes();
    }

    private setDisplaySettingsFromUserPrefs(displaySettings: UserAppMonitoringDisplaySettingsModel) {
        if (!displaySettings) {
            return;
        }
        this.showMentionIndex = displaySettings.showMentionIndex;
        this.searchResultsService.mentionsCompact = displaySettings.isCompactMentionDisplay;
        this.showImages = displaySettings.showImages;
        this.updateMentionViewOptions();
        const latest = this.mentionSearchService.getLatestSearchCriteriaValue();
        if (latest && latest.sortField) {
            this.mentionSearchService.setSortField(latest.sortField);
            this.mentionSearchService.setSortOrder(latest.sortOrder);
        } else {
            this.mentionSearchService.setSortField(monitoringSortTypeToSearchCriteriaSortFieldMap.get(displaySettings.sortType));
            this.mentionSearchService.setSortOrder(displaySettings.sortDirection);
        }

        if (this.isSyndicateMode && (displaySettings.pageSize > 100)) {
            this.mentionSearchService.setPageSize(100);
        } else {
            this.mentionSearchService.setPageSize(displaySettings.pageSize);
        }
    }

    private getUserPreferencesFromRoute(mode: Mode): Observable<UserAppMonitoringDisplaySettingsModel> {
        let getItems$: Observable<MentionSearchModel | Folder>;
        let preferenceType: 'topicsPrefs' | 'savedCoveragePrefs';

        switch (mode) {
            case Mode.topics:
                getItems$ = this.topicsService.getFocusedTopic().pipe(
                    rxjsFilter(x => !!x),
                );
                preferenceType = 'topicsPrefs';
                this.mentionContent = MentionContentCustomizationEnum.summary;
                break;
            case Mode.folders:
                getItems$ = this.folderService.currentResource$.pipe(
                    rxjsFilter(x => !!x),
                );
                preferenceType = 'savedCoveragePrefs';
                this.mentionContent = MentionContentCustomizationEnum.lead;
                break;
            case Mode.allSavedCoverage:
                // Selected folder will be null for All Saved Coverage, so no filter needed
                getItems$ = this.folderService.currentResource$;
                preferenceType = 'savedCoveragePrefs';
                this.mentionContent = MentionContentCustomizationEnum.lead;
                break;
        }

        return combineLatest([
            getItems$,
            this.mentionsService.getMentions().pipe(
                rxjsFilter(x => !!x),
            ),
        ]).pipe(
            switchMap(() => this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs =>  userAppPrefs.monitoringPrefs[preferenceType].defaultDisplaySettings)
            )),
            take(1)
        );
    }

    private setTopicBackfillAttributes(backfills: TopicBackfillModel[]): void {
        if (!backfills) {
            this.backfillAttributesSubject.next([]);
            return;
        }
        backfills.sort((a, b) => +b.createdDate - +a.createdDate);
        this.backfillAttributesSubject.next(backfills.map(
            (backfill, index) => {
                return [
                    {
                        id: `backfill.${index}.requestedDate`,
                        label: 'Monitoring.search.backfill.dateRequested',
                        value: backfill.createdDate,
                        type: attributeTypesEnum.date,
                        icon: backfill.totalMentions ? 'fa-regular fa-duotone fa-circle-check' : 'fa-kit fa-agpr-in-progress',
                        iconToolTip:  backfill.totalMentions
                            ? 'Monitoring.search.backfill.status.completed'
                            : 'Monitoring.search.backfill.status.inProgress'
                    },
                    {
                        id: `backfill.${index}.fromDate`,
                        label: 'Monitoring.search.backfill.fromDate',
                        value: backfill.dateFilter.dateRange.start,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.toDate`,
                        label: 'Monitoring.search.backfill.toDate',
                        value: backfill.dateFilter.dateRange.end,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.mentionCount`,
                        label: 'Mentions',
                        value: backfill.totalMentions,
                        type: attributeTypesEnum.number
                    }
                ];
            }
        ));
    }

    private setSubscriptionBackfillAttributes(): void {
        if (!this.subscription || !this.subscription.backfills) {
            this.backfillAttributesSubject.next([]);
            return;
        }
        const backfills = this.subscription.backfills.slice();
        backfills.sort((a, b) => +b.createdDate - +a.createdDate);
        this.backfillAttributesSubject.next(backfills.map(
            (backfill, index) => {
                return [
                    {
                        id: `backfill.${index}.requestedDate`,
                        label: 'MediaSubscriptions.backfill.dateRequested',
                        value: backfill.createdDate,
                        type: attributeTypesEnum.date,
                        icon: backfill.status === BackfillStatusApiEnum.completed ? 'fa-regular fa-duotone fa-circle-check' : 'fa-kit fa-agpr-in-progress',
                        iconToolTip:  backfill.status === BackfillStatusApiEnum.completed
                            ? 'Monitoring.search.backfill.status.completed'
                            : 'Monitoring.search.backfill.status.inProgress'
                    },
                    {
                        id: `backfill.${index}.fromDate`,
                        label: 'MediaSubscriptions.backfill.fromDate',
                        value: backfill.fromDate,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.toDate`,
                        label: 'MediaSubscriptions.backfill.toDate',
                        value: backfill.toDate,
                        type: attributeTypesEnum.date
                    },
                    {
                        id: `backfill.${index}.mentionCount`,
                        label: 'MediaSubscriptions.backfill.mentions',
                        value: backfill.mentionCount,
                        type: attributeTypesEnum.number
                    }
                ];
            }
        ));
    }

    private getModeFromRoute(route: MonitoringRouteModel): Mode {
        switch (route.type) {
            case RouteType.search:
            case RouteType.results:
                this.onUpdateSelectedFolder(null);
                break;
            case RouteType.topicsHype:
            case RouteType.topic:
                return Mode.topics;
            case RouteType.folder:
            case RouteType.mentionsHype:
                return Mode.folders;
            case RouteType.mentions:
                this.pageHeading = this.localizationService.transform('Monitoring.result.savedCoverage.heading');
                return Mode.allSavedCoverage;
            default:
                return Mode.other;
        }
    }

    onAnimationStart(event: AnimationEvent) {
        this.toolbarStateService.onAnimationStart(event);
    }

    onAnimationDone(event: AnimationEvent) {
        this.toolbarStateService.onAnimationDone(event);
    }

    onScroll(scrollTop: number) {
        this.toolbarStateService.onScroll(scrollTop);
    }


    activate() {
        this.mediaSubscriptionService.activateCurrentSubscription().subscribe();
    }

    pause() {
        this.mediaSubscriptionService.pauseCurrentSubscription().subscribe();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());

        this.mentionSearchService.registerPagingValidator(null);
        if (!this.searchResultsService.filtersLocked() && !this.searchResultsService.getIsViewModeChange()) {
            this.clearFilters();
        }
    }
}
